import React, { createContext, useContext, useState } from "react";

import { StoreApi, createStore, useStore } from "zustand";
import { devtools } from "zustand/middleware";

interface WebsheetDocumentState {
  isSidePanelOpen: boolean;
  setIsSidePanelOpen: (isOpen: boolean) => void;
  sidePanelContent: string; //TODO: Create enum for possible content types
  setSidePanelContent: (id: string) => void;

  isPreCleanseFinished: boolean;
  setIsPreCleanseFinished: (isFinished: boolean) => void;

  isWebsheetSpinnerDisabled: boolean;
  setIsWebsheetSpinnerDisabled: (isDisabled: boolean) => void;

  data: Array<unknown>;
  setData: (data: Array<unknown>) => void;

  currentSheet: unknown;
  setCurrentSheet: (sheet: unknown) => void;
  currentSheetName: string;
  setCurrentSheetName: (sheetName: string) => void;
  setSheetWithErrorTemplate: (
    sheetName: string,
    errorSheetTemplate: unknown
  ) => void;

  cleaningWizardData: unknown;
  setCleaningWizardData: (data: unknown) => void;
}

const createDefaultStore = () =>
  createStore<WebsheetDocumentState>()(
    devtools(
      (set, get) => ({
        data: [],
        setData: (data: Array<unknown>) => {
          const canUseSelectedSheet =
            get().currentSheet !== null &&
            data?.some(d => d.sheet === get().currentSheet.sheet);
          const defaultSheet = canUseSelectedSheet
            ? get().currentSheet
            : (data?.find(d => d.state === "visible") ?? null);
          set(() => ({
            data: data,
            currentSheet: defaultSheet,
            currentSheetName: defaultSheet?.sheet ?? ""
          }));
        },
        cleaningWizardData: {},
        setCleaningWizardData: (data: unknown) =>
          set(() => ({ cleaningWizardData: data })),
        currentSheet: null,
        setCurrentSheet: (sheet: unknown) =>
          set(() => ({
            currentSheet: sheet,
            currentSheetName: sheet?.sheet ?? ""
          })),
        currentSheetName: "",
        setCurrentSheetName: (sheetName: string) => {
          if (get().currentSheet?.sheet === sheetName) {
            return;
          }
          const updateSheet = get().data?.find(d => d.sheet === sheetName);
          set(() => ({
            currentSheet: updateSheet ?? null,
            currentSheetName: updateSheet?.sheet ?? ""
          }));
        },
        setSheetWithErrorTemplate: (
          sheetName: string,
          errorSheetTemplate: unknown
        ) => {
          const sheetIndex = get().data.findIndex(d => d.sheet === sheetName);
          if (sheetIndex === -1) {
            return;
          }
          const updatedData = [...get().data];
          const existingSheet = updatedData[sheetIndex];
          updatedData[sheetIndex] = { ...existingSheet, errorSheetTemplate };
          set(() => ({ data: updatedData }));
        },
        isWebsheetSpinnerDisabled: false,
        setIsWebsheetSpinnerDisabled: (isDisabled: boolean) =>
          set(() => ({ isWebsheetSpinnerDisabled: isDisabled })),
        isSidePanelOpen: false,
        setIsSidePanelOpen: (isOpen: boolean) =>
          set(() => ({ isSidePanelOpen: isOpen })),
        sidePanelContent: "",
        setSidePanelContent: (id: string) =>
          set(() => ({ sidePanelContent: id })),
        isPreCleanseFinished: true,
        setIsPreCleanseFinished: (isFinished: boolean) =>
          set(() => ({ isPreCleanseFinished: isFinished }))
      }),
      { name: "WebsheetDocument" }
    )
  );

interface WebsheetDocumentProviderProps {
  children: React.ReactNode;
}

export const WebsheetDocumentContext =
  createContext<StoreApi<WebsheetDocumentState>>(createDefaultStore());

export const WebsheetDocumentProvider = (
  props: WebsheetDocumentProviderProps
) => {
  const [store] = useState(() => createDefaultStore());

  return (
    <WebsheetDocumentContext.Provider value={store}>
      {props.children}
    </WebsheetDocumentContext.Provider>
  );
};

export const useWebsheetDocument = () => {
  const store = useContext(WebsheetDocumentContext);
  return useStore(store);
};
